import { useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { MapPin, Home, TrendingUp, CheckCircle, ArrowLeft, Phone, Mail, Building2, AlertTriangle, Star } from 'lucide-react'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import { useValuationStore } from '@/store/valuationStore'
import { formatCurrency, formatNumber } from '@/lib/utils'

// Agenzia di riferimento
const AGENCY_NAME = 'L.A. di Elia Soddu'
const AGENCY_ADDRESS = 'Via Sicilia, 22 - Assemini (CA)'
const AGENCY_PHONE = '+39 3288820738'
const AGENCY_EMAIL = 'l.aimm@hotmail.it'

export function Results() {
  const navigate = useNavigate()
  const { result, resetForm } = useValuationStore()

  useEffect(() => {
    if (!result) {
      navigate('/valutazione')
      return
    }

    // Facebook Pixel: traccia conversione Lead al caricamento della pagina risultati
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead')
    }
  }, [result, navigate])

  if (!result) {
    return null
  }

  const handleNewValuation = () => {
    resetForm()
    navigate('/valutazione')
  }

  return (
    <div className="flex-1 bg-gradient-to-b from-gray-50 to-white py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/valutazione"
            className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Nuova valutazione
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            Risultato della Valutazione
          </h1>
          <p className="mt-2 text-gray-600">
            Stima indicativa basata sui dati reali del compravenduto degli ultimi 12 mesi
          </p>
        </div>

        {/* Disclaimer Banner */}
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-amber-800">Questa è una stima indicativa</p>
            <p className="text-amber-700 mt-1">
              Il valore reale dell'immobile può variare in base a caratteristiche specifiche,
              stato di manutenzione, e condizioni di mercato. Per una valutazione professionale
              e accurata, contatta la nostra agenzia di riferimento.
            </p>
          </div>
        </div>

        {/* Main Value Card */}
        <Card className="mb-6 bg-gradient-to-br from-primary-600 to-primary-800 text-white overflow-hidden">
          <CardContent className="py-10 text-center relative">
            <div className="absolute top-4 right-4 bg-white/20 px-3 py-1 rounded-full text-xs">
              Stima indicativa
            </div>
            <p className="text-primary-100 mb-2 text-lg">Valore stimato dell'immobile</p>
            <p className="text-5xl sm:text-6xl font-bold mb-4">
              {formatCurrency(result.estimatedValue)}
            </p>
            <div className="flex items-center justify-center gap-4 text-primary-100">
              <span>Min: {formatCurrency(result.minValue)}</span>
              <span className="w-1.5 h-1.5 bg-primary-300 rounded-full" />
              <span>Max: {formatCurrency(result.maxValue)}</span>
            </div>
            <div className="mt-6">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium text-primary-100 bg-white/20">
                Zona: {result.zoneName}
              </span>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Property Details */}
          <Card className="shadow-md border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="h-5 w-5 text-primary-600" />
                Dettagli Immobile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Zona</span>
                <span className="text-gray-900 font-medium text-right max-w-[200px] truncate">
                  {result.zoneName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tipo</span>
                <span className="text-gray-900 font-medium capitalize">
                  {result.formData.propertyType}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Superficie</span>
                <span className="text-gray-900 font-medium">
                  {formatNumber(result.formData.area)} mq
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Prezzo al mq</span>
                <span className="text-gray-900 font-medium">
                  {formatCurrency(result.pricePerSqm)}/mq
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Zone Info */}
          <Card className="shadow-md border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary-600" />
                Zona
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Zona</span>
                <span className="text-gray-900 font-medium">{result.zoneName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Prezzo base zona</span>
                <span className="text-gray-900 font-medium">
                  {formatCurrency(result.prezzoBaseMq)}/mq
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Range stima</span>
                <span className="text-gray-900 font-medium">
                  {formatCurrency(result.minValue)} - {formatCurrency(result.maxValue)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Applied Coefficients */}
        {result.appliedCoefficients.length > 0 && (
          <Card className="mb-6 shadow-md border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary-600" />
                Fattori considerati nella valutazione
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {result.appliedCoefficients.map((coef, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-gray-700">{coef.description}</span>
                    </div>
                    <span
                      className={`font-medium ${
                        coef.value >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {coef.value >= 0 ? '+' : ''}
                      {(coef.value * 100).toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Agency CTA */}
        <Card className="mb-6 bg-gradient-to-r from-gray-900 to-gray-800 text-white overflow-hidden shadow-xl">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="shrink-0">
                <p className="text-xs uppercase tracking-wider text-primary-300 mb-1">Agenzia di riferimento</p>
                <span className="text-2xl font-bold text-white block">{AGENCY_NAME}</span>
                <span className="text-sm text-primary-200">L.A. immobiliare</span>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-bold mb-2">
                  Vuoi una valutazione professionale?
                </h3>
                <p className="text-gray-300 mb-4">
                  La nostra agenzia di riferimento è a tua disposizione per una valutazione accurata
                  e personalizzata del tuo immobile. Contattala senza impegno!
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-3 text-sm text-gray-400">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {AGENCY_ADDRESS}
                  </span>
                  <span className="hidden sm:block">•</span>
                  <span className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-yellow-500" />
                    Esperienza dal 2010
                  </span>
                </div>
              </div>
              <div className="shrink-0 flex flex-col gap-3">
                <a href={`tel:${AGENCY_PHONE}`}>
                  <Button size="lg" className="w-full bg-primary-600 hover:bg-primary-700">
                    <Phone className="h-4 w-4 mr-2" />
                    Chiama ora
                  </Button>
                </a>
                <a href={`mailto:${AGENCY_EMAIL}?subject=Richiesta valutazione immobile&body=Buongiorno, ho effettuato una valutazione online per il mio immobile nella zona ${result.zoneName}. Vorrei ricevere maggiori informazioni per una valutazione professionale.`}>
                  <Button variant="outline" size="lg" className="w-full bg-white text-gray-800 hover:bg-gray-100">
                    <Mail className="h-4 w-4 mr-2" />
                    Scrivici
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button onClick={handleNewValuation} size="lg" variant="outline">
            <Building2 className="h-4 w-4 mr-2" />
            Nuova valutazione
          </Button>
          <Button variant="outline" size="lg" onClick={() => window.print()}>
            Stampa risultato
          </Button>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 p-4 bg-gray-100 rounded-lg">
          <p className="text-center text-sm text-gray-600">
            <strong>Nota importante:</strong> Questa valutazione è indicativa e basata sui dati reali
            del compravenduto degli ultimi 12 mesi nella zona metropolitana di Cagliari. Non costituisce
            perizia estimativa ufficiale ai sensi di legge. Il valore effettivo dell'immobile
            può variare significativamente in base a fattori non considerati in questa stima.
            Per vendite, acquisti o pratiche legali, si consiglia di richiedere una perizia
            professionale presso la nostra agenzia di riferimento.
          </p>
        </div>
      </div>
    </div>
  )
}
